import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  outboundWorkflowService,
  type OutboundStage,
  type OutboundWorkflow,
} from '@/services/outboundWorkflowService';
import { OutboundWorkflowFlow, StageExits } from './OutboundWorkflowFlow';

export const OutboundWorkflowsPage: React.FC = () => {
  const { t } = useLanguage();
  const [workflows, setWorkflows] = useState<OutboundWorkflow[]>([]);
  const [workflowId, setWorkflowId] = useState('');
  const [stages, setStages] = useState<OutboundStage[]>([]);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const wfs = await outboundWorkflowService.listWorkflows();
      setWorkflows(wfs);
      if (wfs[0]) {
        setWorkflowId(wfs[0].id);
        setStages(await outboundWorkflowService.listStages(wfs[0].id));
      }
    })();
  }, []);

  const loadStages = async (id: string) => {
    setWorkflowId(id);
    setStages(await outboundWorkflowService.listStages(id));
  };

  const save = async (stage: OutboundStage, patch: Partial<OutboundStage>) => {
    setSaving(stage.id);
    const updated = await outboundWorkflowService.updateStage(stage.id, patch);
    setStages((prev) =>
      prev.map((s) => (s.id === stage.id ? { ...s, ...updated, exits: s.exits } : s))
    );
    setSaving(null);
  };

  return (
    <main className="space-y-3">
      <h1 className="text-lg font-semibold text-[var(--color-primary-font)]">{t('outbound.workflows.title')}</h1>
      <label className="block text-sm max-w-sm">
        Workflow
        <select
          className="mt-1 w-full border rounded px-2 py-1"
          value={workflowId}
          onChange={(e) => void loadStages(e.target.value)}
        >
          {workflows.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
      </label>
      <OutboundWorkflowFlow stages={stages} />
      <h2 className="text-sm font-medium">{t('outbound.workflows.stages')}</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left border-b">
              <th className="py-1 pr-2">Name</th>
              <th className="py-1 pr-2">Kind</th>
              <th className="py-1 pr-2">Channel</th>
              <th className="py-1 pr-2">Delay (d)</th>
              <th className="py-1 pr-2">Then (how T-MOS picks next)</th>
              <th className="py-1 pr-2">Script</th>
            </tr>
          </thead>
          <tbody>
            {stages.map((s) => (
              <tr key={s.id} className="border-b align-top">
                <td className="py-1 pr-2">
                  <input
                    className="w-full border rounded px-1 py-0.5"
                    defaultValue={s.name}
                    onBlur={(e) => {
                      if (e.target.value !== s.name) void save(s, { name: e.target.value });
                    }}
                  />
                  <div className="text-xs opacity-70">{s.code}</div>
                </td>
                <td className="py-1 pr-2">
                  <select
                    className="border rounded px-1 py-0.5"
                    value={s.kind}
                    onChange={(e) => void save(s, { kind: e.target.value })}
                  >
                    {['do', 'wait', 'review', 'exit'].map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="py-1 pr-2">
                  <select
                    className="border rounded px-1 py-0.5"
                    value={s.channel}
                    onChange={(e) => void save(s, { channel: e.target.value })}
                  >
                    {['linkedin', 'email', 'call', 'meeting', 'none'].map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="py-1 pr-2">
                  <input
                    type="number"
                    className="w-16 border rounded px-1 py-0.5"
                    defaultValue={s.delay_after_complete_days ?? ''}
                    onBlur={(e) => {
                      const n = e.target.value === '' ? null : Number(e.target.value);
                      void save(s, { delay_after_complete_days: n });
                    }}
                  />
                </td>
                <td className="py-1 pr-2 max-w-xs">
                  <StageExits stage={s} stages={stages} />
                </td>
                <td className="py-1 pr-2">
                  <textarea
                    className="w-full min-w-[16rem] border rounded px-1 py-0.5 text-xs"
                    rows={3}
                    defaultValue={s.script_text || ''}
                    onBlur={(e) => {
                      if (e.target.value !== (s.script_text || '')) {
                        void save(s, { script_text: e.target.value });
                      }
                    }}
                  />
                  {saving === s.id && <span className="text-xs">Saving…</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  );
};
